import { IEvaServiceDefinition } from '@springtree/eva-services-core';
import { AnyAction } from 'redux';
import { IStateOptions } from './options';

/**
 * The data payload for an EVA Response Redux action
 *
 * @export
 * @interface IEvaServiceResponseActionPayload
 * @extends {AnyAction}
 * @template SVC
 */
export interface IEvaServiceResponseActionPayload<SVC extends IEvaServiceDefinition> {
  serviceContext: string;
  chainContext: string;
  data: SVC['response'];
}

export interface IEvaServiceResponseAction<SVC extends IEvaServiceDefinition, TYPE = string> extends AnyAction {
  type: TYPE;
  payload: IEvaServiceResponseActionPayload<SVC>;
}

/**
 * The required payload for making a response action
 *
 * @export
 * @interface ICreateServiceResponseAction
 * @template SVC
 */
export interface ICreateServiceResponseActionParams<SVC extends IEvaServiceDefinition> {
  /**
   * The EVA Service repository definition
   *
   */
  service: new () => SVC;

  /**
   * The typed response payload
   *
   * @type {SVC['response']}
   */
  data?: SVC['response'];

  /**
   * Optional configuration parameters to for the action and state engine
   *
   * @type {IStateOptions}
   */
  stateOptions?: IStateOptions;

  /**
   * Each service call has a unique context identifier
   *
   * @type {string}
   */
  serviceContext: string;

  /**
   * If a service request is part of a chain of calls this chainContext will be used
   * to identify the chain
   *
   * @type {string}
   */
  chainContext: string;
}

/**
 * Helper method to create a typed EVA Service response action
 *
 * @export
 * @template SVC
 * @param {ICreateServiceResponseAction<SVC>} params The response action parameters
 * @returns
 */
export function createServiceResponseAction<SVC extends IEvaServiceDefinition>(
  params: ICreateServiceResponseActionParams<SVC>,
) {

  // Determine the state key name either by the service name or the provided
  // custom state key name
  //
  const serviceName = (params.stateOptions && params.stateOptions.stateKey) || new params.service().name;

  // Combine all supplied parameters into the action payload
  //
  const payload: IEvaServiceResponseActionPayload<SVC> = {
    serviceContext: params.serviceContext,
    chainContext: params.chainContext,
    data: params.data,
  };

  // Build the final action object
  //
  const action: IEvaServiceResponseAction<SVC> = {
    payload,
    type: `${serviceName}:RESPONSE`,
  };

  return action;
}
